import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export const TermsOfService: React.FC = () => {
    const location = useLocation();
    const isFromSignup = new URLSearchParams(location.search).get('source') === 'signup';

    return (
        <div className="min-h-screen bg-finance-darker py-20 px-4">
            <Helmet>
                <title>Terms of Service — ForexFundAI</title>
                <meta name="description" content="Review the ForexFund AI Terms of Service for institutional signal usage, autotrade policies, and user obligations. Understand our platform rules and legal agreements." />
                <link rel="canonical" href="https://www.forexfundai.com/terms" />
            </Helmet>
            <div className="max-w-4xl mx-auto bg-finance-card border border-gray-800 rounded-3xl p-8 sm:p-12 shadow-2xl">
                <h1 className="text-4xl font-black text-white mb-8">Terms of Service</h1>
                <p className="text-gray-400 mb-6 text-sm italic">Last Updated: March 8, 2026</p>

                <div className="space-y-8 text-gray-300 leading-relaxed mb-12">
                    <section>
                        <h2 className="text-xl font-bold text-white mb-4">1. Acceptance of Terms</h2>
                        <p>
                            By accessing or using ForexFund AI ("the Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, you may not use the Service.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-bold text-white mb-4">2. Description of Service</h2>
                        <p>
                            ForexFund AI provides AI-driven market analysis, trading signals, and institutional data insights. The Service is for informational purposes only and does not constitute financial, investment, or trading advice.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-bold text-white mb-4">3. User Obligations</h2>
                        <ul className="list-disc pl-5 space-y-2">
                            <li>You must be at least 18 years old to use this Service.</li>
                            <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
                            <li>You agree not to use the Service for any illegal or unauthorized purpose.</li>
                            <li>Redistribution of signals or analytical data without explicit written consent is strictly prohibited.</li>
                        </ul>
                    </section>

                    <section id="refund">
                        <h2 className="text-xl font-bold text-white mb-4">4. Subscription and Refund Policy</h2>
                        <div className="space-y-4">
                            <p>
                                Subscribed services are billed in advance. Due to the nature of digital information services and the instant delivery of proprietary analytical data, ForexFund AI generally operates a strictly <strong>NO REFUND</strong> policy.
                            </p>
                            <div className="p-4 bg-finance-accent/5 border border-finance-accent/20 rounded-xl">
                                <p className="text-sm font-bold text-finance-accent mb-2 uppercase tracking-wide">Standard Refund Conditions:</p>
                                <p className="text-sm">
                                    A refund may only be issued if <strong>BOTH</strong> of the following conditions are met:
                                </p>
                                <ul className="list-disc pl-5 mt-2 space-y-1 text-sm">
                                    <li>The refund request is submitted within <strong>24 hours</strong> of the initial subscription payment.</li>
                                    <li>The user has <strong>NOT accessed or utilized</strong> any analytical tools, institutional scanners, or live signal feeds during that period.</li>
                                </ul>
                            </div>
                            <p className="text-sm italic text-gray-400">
                                If an analytical tool or scanner has been viewed or utilized even once, or if more than 24 hours have passed since the subscription purchase, the user is no longer eligible for a refund.
                            </p>
                            <p>
                                You may cancel your subscription at any time to prevent future billing, but access will continue until the end of the current paid billing period.
                            </p>
                        </div>
                    </section>

                    <section>
                        <h2 className="text-xl font-bold text-white mb-4">5. Intellectual Property</h2>
                        <p>
                            All content, algorithms, signals, and software provided through ForexFund AI are the exclusive property of ForexFund AI and its licensors. You are granted a limited, non-exclusive license to use the Service for personal, non-commercial use.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-bold text-white mb-4">6. Limitation of Liability</h2>
                        <p>
                            ForexFund AI and its affiliates shall not be liable for any direct, indirect, incidental, or consequential damages resulting from the use or inability to use the Service, including but not limited to trading losses.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-bold text-white mb-4">7. Termination</h2>
                        <p>
                            We reserve the right to terminate or suspend your account at our sole discretion, without notice, for conduct that we believe violates these Terms or is harmful to other users or the Service.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-bold text-white mb-4">8. Governing Law</h2>
                        <p>
                            These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which the company operates, without regard to its conflict of law provisions.
                        </p>
                    </section>
                </div>

                <div className="pt-8 border-t border-gray-800 flex justify-center">
                    <Link
                        to={isFromSignup ? "/login?view=signup" : "/"}
                        className="flex items-center gap-2 px-6 py-3 bg-gray-900 border border-gray-800 rounded-xl text-white font-bold hover:bg-gray-800 hover:border-gray-700 transition-all group"
                    >
                        <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                        {isFromSignup ? "Back to Sign Up" : "Back to Home"}
                    </Link>
                </div>
            </div>
        </div>
    );
};

